/**
 * Cedula de identidad uruguaya (ADR-014).
 *
 * La cedula es el identificador unico de negocio del cliente. Se guarda **normalizada**:
 * solo digitos, sin puntos ni guion, con el digito verificador validado.
 *
 * El verificador usa los pesos 2, 9, 8, 7, 6, 3, 4 sobre los siete digitos del numero,
 * y el digito de control es el complemento a 10 de la suma modulo 10. Validarlo atrapa
 * la enorme mayoria de los errores de tipeo del mostrador antes de que se conviertan en
 * un cliente duplicado.
 */
import { errorDominio } from '../tipos/errores.js';
import { exito, fallo } from '../tipos/resultado.js';
/** Pesos del algoritmo del digito verificador. */
const PESOS = [2, 9, 8, 7, 6, 3, 4];
/** Solo se aceptan digitos, puntos, guiones y espacios en la entrada. */
const CARACTERES_ADMITIDOS = /^[\d.\-\s]+$/;
/**
 * Digito verificador que le corresponde a un numero de cedula.
 *
 * @param cuerpo Los digitos SIN el verificador, 7 como maximo.
 */
export function calcularDigitoVerificador(cuerpo) {
    if (!/^\d{1,7}$/.test(cuerpo)) {
        return fallo(errorDominio('CEDULA_INVALIDA', 'cedula'));
    }
    const rellenado = cuerpo.padStart(7, '0');
    let suma = 0;
    for (let indice = 0; indice < 7; indice += 1) {
        const digito = Number(rellenado[indice]);
        const peso = PESOS[indice];
        if (peso === undefined) {
            return fallo(errorDominio('CEDULA_INVALIDA', 'cedula'));
        }
        suma += digito * peso;
    }
    return exito((10 - (suma % 10)) % 10);
}
/**
 * Normaliza y valida una cedula.
 *
 * Acepta las formas en que la gente la escribe —`1.111.111-1`, `1111111-1`,
 * `1 111 111 1`— y devuelve siempre la misma cadena de digitos. Rechaza el verificador
 * incorrecto.
 */
export function normalizarCedula(entrada, campo = 'cedula') {
    const recortada = entrada.trim();
    if (recortada === '' || !CARACTERES_ADMITIDOS.test(recortada)) {
        return fallo(errorDominio('CEDULA_INVALIDA', campo));
    }
    const digitos = recortada.replace(/[.\-\s]/g, '');
    // Menos de 7 digitos es una cedula antigua muy corta; mas de 8, no existe.
    if (!/^\d{7,8}$/.test(digitos)) {
        return fallo(errorDominio('CEDULA_INVALIDA', campo));
    }
    const cuerpo = digitos.slice(0, -1);
    const verificadorDeclarado = Number(digitos.slice(-1));
    const esperado = calcularDigitoVerificador(cuerpo);
    if (!esperado.ok) {
        return fallo(errorDominio('CEDULA_INVALIDA', campo));
    }
    if (esperado.valor !== verificadorDeclarado) {
        return fallo(errorDominio('CEDULA_INVALIDA', campo));
    }
    return exito(digitos);
}
/** Texto plano de la cedula normalizada. Para persistir; nunca para registrar en un log. */
export function valorCedula(cedula) {
    return cedula;
}
/**
 * Enmascara una cedula para mostrarla o registrarla sin exponerla.
 *
 * Deja visibles solo los dos ultimos digitos: alcanzan para que una persona confirme
 * cual de sus documentos es, y no alcanzan para reconstruirlo.
 * `11111111` se convierte en `******11`.
 *
 * Los logs y las trazas del sistema no imprimen cedulas completas (docs/SEGURIDAD.md §6).
 */
export function enmascararCedula(cedula) {
    const visibles = 2;
    return '*'.repeat(cedula.length - visibles) + cedula.slice(-visibles);
}
/**
 * Formatea una cedula para mostrarla completa: `1.111.111-1`.
 *
 * Solo para pantallas del panel donde el operador necesita verla entera. No usar en
 * logs, mensajes de error ni exportaciones.
 */
export function formatearCedula(cedula) {
    const cuerpo = cedula.slice(0, -1);
    const verificador = cedula.slice(-1);
    const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return `${conPuntos}-${verificador}`;
}
//# sourceMappingURL=cedula.js.map